import type { StreamParser } from "@codemirror/language";

const regoKeywords = new Set(["as", "contains", "data", "default", "else", "every", "if", "in", "import", "input", "package", "not", "some", "with"]);

const regoConstants = new Set(["true", "false", "null"]);

interface RegoState {
    inRawString: boolean;
}

export const rego: StreamParser<RegoState> = {
    name: "rego",

    startState(): RegoState {
        return { inRawString: false };
    },

    token(stream, state): string | null {
        // Continue raw string from previous line
        if (state.inRawString) {
            while (!stream.eol()) {
                if (stream.next() === "`") {
                    state.inRawString = false;
                    return "string";
                }
            }
            return "string";
        }

        if (stream.eatSpace()) return null;

        // Comments: # to end of line
        if (stream.match("#")) {
            stream.skipToEnd();
            return "comment";
        }

        // Double-quoted strings (with escape sequences)
        if (stream.match('"')) {
            while (!stream.eol()) {
                const ch = stream.next();
                if (ch === "\\") {
                    stream.next();
                } else if (ch === '"') {
                    return "string";
                }
            }
            return "string";
        }

        // Raw strings (backtick-delimited, can span multiple lines)
        if (stream.match("`")) {
            while (!stream.eol()) {
                if (stream.next() === "`") {
                    return "string";
                }
            }
            state.inRawString = true;
            return "string";
        }

        // Numbers
        if (stream.match(/^[0-9]+(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?/)) {
            return "number";
        }

        // Words: keywords, constants, or plain identifiers
        if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)) {
            const word = stream.current();
            if (regoKeywords.has(word)) return "keyword";
            if (regoConstants.has(word)) return "atom";
            return "variableName";
        }

        // Brackets
        if (stream.match(/^[{}[\]()]/)) {
            return "bracket";
        }

        // Multi-character operators
        if (stream.match(/^(?::=|!=|>=|<=|==)/)) {
            return "operator";
        }

        // Single-character operators
        if (stream.match(/^[=<>+\-*/%&|]/)) {
            return "operator";
        }

        // Punctuation
        if (stream.match(/^[,.:]/)) {
            return "punctuation";
        }

        // Any other character
        stream.next();
        return null;
    },

    languageData: {
        commentTokens: { line: "#" },
        closeBrackets: { brackets: ["{", "[", "(", '"', "`"] },
    },
};
